/**
 * Raise Alert feature — loads the rules and runs a scan.
 *
 * Rules come from the backend (`critical_alert`, `critical_alert_lexicon` and
 * `critical_alert_modality_map`). The bundled JSON stays in the build as the
 * fail-open fallback: if the API is unreachable we still scan rather than
 * silently doing nothing, and a failed call can never block a report submit.
 *
 * The feature flag is resolved by the backend against the user's firm. When it
 * says disabled we do not scan at all — the fallback is only for a call that
 * failed, never for a firm that is switched off.
 */
import bundledRules from "./criticalAlertRules.json";
import bundledLexicon from "./criticalAlertLexicon.json";
import bundledModalityMap from "./criticalAlertModalityMap.json";
import {
  scanReport,
  resolveEngineModalities,
  ruleAppliesToModalities,
} from "./criticalAlertEngine";
import { fetchCriticalAlertConfig } from "../../service/CriticalAlertService";

let cache = null;

/**
 * The backend returns section headings as two flat groups, SECTION_HISTORY and
 * SECTION_FINDINGS, because that is one row per phrase in the database. The
 * engine wants them nested under SECTION_HEADER, which is how the bundled JSON
 * is shaped. Convert rather than change the engine, so the bundled fallback and
 * the API produce identical behaviour and the same tests cover both.
 */
const normaliseLexicon = (lexicon) => {
  if (!lexicon) {
    return bundledLexicon;
  }
  const out = { ...lexicon };
  const history = lexicon.SECTION_HISTORY?.phrases;
  const findings = lexicon.SECTION_FINDINGS?.phrases;

  if (history || findings) {
    out.SECTION_HEADER = {
      historyHeaders: history || [],
      findingsHeaders: findings || [],
    };
    delete out.SECTION_HISTORY;
    delete out.SECTION_FINDINGS;
  }
  return out;
};

/** The API sends a flat array; the engine expects { entries: [...] }. */
const normaliseModalityMap = (modalityMap) => {
  if (!modalityMap) {
    return bundledModalityMap;
  }
  if (Array.isArray(modalityMap)) {
    return { entries: modalityMap };
  }
  return modalityMap;
};

const bundledConfig = (reason) => ({
  enabled: true,
  rules: bundledRules.rules,
  lexicon: bundledLexicon,
  modalityMap: bundledModalityMap,
  version: `bundled-${bundledRules.version}`,
  source: `bundled (${reason})`,
});

/**
 * Loads the configuration once per session. Never throws.
 *
 * @returns {{enabled:boolean, rules:array, lexicon:object, modalityMap:object,
 *            version:string, source:string}}
 */
export const loadCriticalAlertConfig = async () => {
  if (cache) {
    // logged so a cache hit is never mistaken for "the scan did nothing" —
    // the config is held for the whole page session, so a flag change in the
    // database needs a page reload to be picked up
    console.warn(
      `[Raise Alert feature] using cached config — enabled=${cache.enabled}, ` +
        `${cache.rules.length} rules, source: ${cache.source}. ` +
        `Reload the page to refetch.`
    );
    return cache;
  }

  const payload = await fetchCriticalAlertConfig();

  // the call failed — fall back to the bundled copy so we still scan
  if (!payload) {
    cache = bundledConfig("API unreachable");
    console.warn("[Raise Alert feature] using bundled rules —", cache.source);
    return cache;
  }

  // the backend says this firm is not switched on
  if (payload.enabled === false) {
    cache = {
      enabled: false,
      rules: [],
      lexicon: {},
      modalityMap: { entries: [] },
      version: payload.version || "disabled",
      source: "backend (disabled for this firm)",
    };
    console.warn("[Raise Alert feature] feature is off for this firm — no scanning");
    return cache;
  }

  // a successful but empty response means the tables were never seeded; the
  // bundled copy is more useful than no rules at all
  if (!payload.rules || payload.rules.length === 0) {
    cache = bundledConfig("API returned no rules");
    console.warn("[Raise Alert feature] using bundled rules —", cache.source);
    return cache;
  }

  cache = {
    enabled: true,
    rules: payload.rules,
    lexicon: normaliseLexicon(payload.lexicon),
    modalityMap: normaliseModalityMap(payload.modalityMap),
    version: payload.version,
    source: "backend",
  };
  console.warn(
    `[Raise Alert feature] loaded ${cache.rules.length} rules from backend, version ${cache.version}`
  );
  return cache;
};

/**
 * The catalogue for the modal's "Search a finding" picker. Active rules only.
 *
 * Pass the study's { modality, studyDescription } and the list is narrowed to the
 * alerts that could actually apply to it -- a chest X-ray stops offering "2nd degree
 * AV block - Mobitz type II" and the other ECG, brain and abdomen rules. Filtering
 * uses ruleAppliesToModalities(), the SAME test the scan uses, so the picker can
 * never offer an alert the scan would refuse to raise on this study.
 *
 * Called with nothing, it returns everything, exactly as before.
 *
 * Fails OPEN: if the modality cannot be resolved to any engine modality, the full
 * list is returned rather than an empty picker. An unrecognised study description
 * must not leave the radiologist unable to raise an alert by hand -- and free text
 * is still there as the final escape hatch either way.
 */
export const getAlertCatalogue = async (studyContext) => {
  const config = await loadCriticalAlertConfig();
  const active = (config.rules || []).filter((rule) => rule.active !== false);

  let applicable = active;
  if (studyContext && (studyContext.modality || studyContext.studyDescription)) {
    const engineModalities = resolveEngineModalities(studyContext, config.modalityMap);
    const resolved = Array.from(engineModalities || []);
    if (resolved.length > 0) {
      const narrowed = active.filter((rule) =>
        ruleAppliesToModalities(rule, resolved, studyContext)
      );
      // an empty result means the filter, not the study, is wrong -- keep the full list
      if (narrowed.length > 0) {
        applicable = narrowed;
      } else {
        console.warn(
          "[Raise Alert feature] no alerts matched this study, offering the full list:",
          JSON.stringify(studyContext)
        );
      }
    }
    console.warn(
      `[Raise Alert feature] picker narrowed to ${applicable.length} of ${active.length} ` +
        `alerts for [${resolved.join(", ")}]`
    );
  }

  return applicable
    .map((rule) => ({
      alertCode: rule.alertCode,
      findingName: rule.findingName,
      priority: rule.priority,
    }))
    .sort((a, b) => a.findingName.localeCompare(b.findingName));
};

/**
 * alertCode -> priority, for colouring the studylist ALERT RAISED badge.
 *
 * study.raise_alert already holds the WORST finding on the study -- the backend's
 * pickPrimaryCode() stores the highest-priority one -- so a single lookup gives the
 * badge its colour without fetching that study's findings.
 *
 * Memoised at module level, not per component. The studylist renders this cell once
 * per row, and every row shares this one promise and one Map.
 *
 * Never rejects: on failure it resolves to an empty Map, and every badge falls back
 * to the neutral colour it had before it was coloured.
 */
let alertPriorityMapPromise = null;
/* NO LONGER USED 10-Sep-2026. The studylist badge derived its colour from this map,
 * keyed by alertCode -- which could never resolve a finding the radiologist typed in
 * themselves, because that has no alertCode. The studylist row now carries
 * raiseAlertPriority, read live from study_critical_alert. Kept because it is the only
 * way to get code -> priority on the client, and the alert catalogue still needs it if
 * anything else ever wants that lookup. */
export const getAlertPriorityMap = () => {
  if (!alertPriorityMapPromise) {
    alertPriorityMapPromise = getAlertCatalogue()
      .then((catalogue) => new Map(catalogue.map((r) => [r.alertCode, r.priority])))
      .catch((error) => {
        console.warn(
          "[Raise Alert feature] could not build the alert priority map — badges stay neutral:",
          error?.message
        );
        return new Map();
      });
  }
  return alertPriorityMapPromise;
};

/**
 * Scans a report. Never throws — on any failure it returns an empty result so
 * Submit proceeds untouched (AC IT-8).
 *
 * @returns {{fired:array, suppressed:array, engineModalities:array,
 *            scanMs:number, ok:boolean, enabled:boolean}}
 */
export const scanReportForCriticalFindings = async (input) => {
  const empty = {
    fired: [],
    suppressed: [],
    engineModalities: [],
    scanMs: 0,
    ok: false,
    enabled: false,
  };

  try {
    const config = await loadCriticalAlertConfig();

    /* The firm-level switch, resolved per study from the study's source firm and
     * passed in by the caller. Explicit false only: undefined or null mean the payload
     * did not carry it, and the backend default is ON, so treating a missing value as
     * off would silently disable the feature everywhere on any load path that has not
     * been wired yet. A firm that has genuinely opted out always sends false. */
    if (input?.criticalAlertEnabled === false) {
      console.warn(
        "[Raise Alert feature] the scan is switched off for this study's firm — not scanning"
      );
      return { ...empty, ok: true };
    }

    if (!config.enabled) {
      return { ...empty, ok: true };
    }

    const result = scanReport(input, {
      rules: config.rules,
      lexicon: config.lexicon,
      modalityMap: config.modalityMap,
    });

    console.warn(
      `[Raise Alert feature] scan done — modality=${input?.modality} desc="${input?.studyDescription}" ` +
        `engineModalities=[${result.engineModalities.join(", ")}] ` +
        `fired=${result.fired.length} suppressed=${result.suppressed.length} ` +
        `chars=${result.textLength} in ${result.scanMs}ms [rules: ${config.source}]`
    );
    if (result.suppressed.length > 0) {
      console.warn(
        "[Raise Alert feature] suppressed:",
        result.suppressed.map((s) => `${s.alertCode} (${s.layer}: ${s.phrase})`)
      );
    }

    return { ...result, ok: true, enabled: true, rulesVersion: config.version };
  } catch (error) {
    // a broken scan must never stop a radiologist submitting a report
    console.warn(
      "[Raise Alert feature] scan failed — submitting without alert check:",
      error
    );
    return empty;
  }
};